import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { handleChunkLoadError, isChunkLoadError } from '@/utils/chunkLoadHandler';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Auto-recover from stale chunks after a new deployment
    if (handleChunkLoadError(error)) {
      return;
    }
    
    // Dismiss splash screen immediately if it was still active
    try {
      const splash = document.getElementById('splash-screen');
      if (splash && splash.parentNode) {
        splash.parentNode.removeChild(splash);
      }
    } catch (_) {}

    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    // React.lazy caches rejected imports. Resetting the boundary alone would
    // render the same rejected component again; reload its webpack runtime too.
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onHome={() => { window.location.href = '/#/'; }}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Error Fallback UI Component
 */
function ErrorFallback({ error, errorInfo, onReset, onHome }) {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center min-h-screen bg-[hsl(var(--main-bg))] p-4">
      <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] max-w-2xl w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <AlertCircle className="w-8 h-8 text-red-400" aria-hidden="true" />
            <CardTitle className="text-2xl text-foreground selectable-text">Something went wrong</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-foreground">
              We're sorry, but something unexpected happened. Don't worry, your progress has been saved.
            </p>
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-4 p-4 bg-[hsl(var(--main-bg))] rounded-lg border border-[hsl(var(--card-border))]">
                <summary className="cursor-pointer text-sm font-medium text-foreground mb-2">
                  Error Details (Development Only)
                </summary>
                <pre className="text-xs text-muted-foreground selectable-text overflow-auto max-h-48 mt-2">
                  {error.toString()}
                  {errorInfo?.componentStack && (
                    <>
                      {'\n\n'}
                      {errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              onClick={onReset}
              variant="outline"
              className="border-[hsl(var(--card-border))] text-foreground flex-1"
              aria-label="Try again"
            >
              <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
              Try Again
            </Button>
            <Button
              onClick={() => {
                onHome();
                onReset();
              }}
              className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white flex-1"
              aria-label="Go to quests dashboard"
            >
              <Home className="w-4 h-4 mr-2" aria-hidden="true" />
              Go to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Export as functional component wrapper
export default function ErrorBoundary({ children }) {
  return <ErrorBoundaryClass>{children}</ErrorBoundaryClass>;
}
